/**
 * Day route optimizer presentation (tracker item 41, phase 3). Pure
 * functions, unit-tested in optimizeUi.test.ts, plus the apply call's
 * outcome mapping (a 409 means someone changed the day: refresh).
 */

import axios from 'axios';
import type { IDispatchApi } from '../api';
import * as types from '../api/types';
import { getErrorCode } from '../utils/apiError';
import { formatDuration, formatTime } from './dispatchUi';

export const OPTIMIZE_CHANGED_MESSAGE = 'Someone else changed this day. The board has been refreshed.';

/** The headline for a preview's outcome. */
export function optimizeOutcomeText(result: Pick<types.OptimizeDayResult, 'outcome' | 'minutesSaved' | 'changes'>): string {
  switch (result.outcome) {
    case 'IMPROVED': {
      const moved = result.changes.length;
      return `Saves about ${formatDuration(result.minutesSaved)} of driving; ${moved} job${moved === 1 ? '' : 's'} move.`;
    }
    case 'ALREADY_BEST':
      return 'This day is already in the shortest order found. Nothing to change.';
    case 'NO_FEASIBLE_ORDER':
      return 'No order fits the kept times, arrival windows, working hours and time off. Try keeping fewer times.';
    case 'NOTHING_TO_MOVE':
      return 'Nothing can move: every timed job keeps its time (or the day has none).';
    case 'DAY_OFF':
      return 'The technician is not working this day.';
    default:
      return String(result.outcome);
  }
}

/** Why a stop keeps its time. */
export function pinReasonText(reason: types.OptimizerPinReason | null): string {
  switch (reason) {
    case 'IN_PROGRESS': return 'In progress';
    case 'DONE': return 'Finished';
    case 'KEPT': return 'Time kept';
    case 'PASSED': return 'Time already passed';
    default: return '';
  }
}

/** Whether the office may tick or untick "keep time" (the server pins the others itself). */
export function canToggleKeep(job: Pick<types.OptimizeDayJob, 'pinReason'>): boolean {
  return job.pinReason === null || job.pinReason === 'KEPT';
}

/** "9:00 AM → 8:05 AM", or just the time when it does not change. */
export function timeChangeText(job: Pick<types.OptimizeDayJob, 'oldStartTime' | 'newStartTime'>): string {
  return job.oldStartTime === job.newStartTime
    ? formatTime(job.newStartTime)
    : `${formatTime(job.oldStartTime)} → ${formatTime(job.newStartTime)}`;
}

/** The drive into a stop: "10 min drive (3.1 mi)", "15 min drive (estimated)" or "Start of the day". */
export function travelInText(job: Pick<types.OptimizeDayJob, 'travelInMinutes' | 'travelInUnknown' | 'travelInDistance'>, unit: string): string {
  if (job.travelInUnknown) return `${job.travelInMinutes} min drive (estimated, no map location)`;
  if (job.travelInMinutes === 0) return 'No drive';
  const distance = job.travelInDistance === null ? '' : ` (${job.travelInDistance} ${unit === 'KM' ? 'km' : 'mi'})`;
  return `${job.travelInMinutes} min drive${distance}`;
}

/** "Driving 1h 10m → 30m", or the current total when nothing changes. */
export function travelSummaryText(result: Pick<types.OptimizeDayResult, 'improved' | 'current' | 'proposed'>): string {
  const before = formatDuration(result.current.totalTravelMinutes);
  return result.improved
    ? `Driving ${before} → ${formatDuration(result.proposed.totalTravelMinutes)}`
    : `Driving ${before}`;
}

/** The pins to send after toggling "keep time" on one job. */
export function toggledPins(pins: string[], jobId: string): string[] {
  return pins.includes(jobId) ? pins.filter((id) => id !== jobId) : [...pins, jobId].sort();
}

export type ApplyOptimizeOutcome =
  | { status: 'applied'; result: types.ApplyDayOptimizationResult }
  /** 409 JOB_CHANGED or SUGGESTION_STALE: nothing was written; refresh the board */
  | { status: 'changed'; staleJobIds: string[] };

/** Apply a preview's changes in one call. Any 409 comes back as 'changed'; other errors throw. */
export async function applyOptimization(
  api: Pick<IDispatchApi, 'applyDayOptimization'>,
  preview: Pick<types.OptimizeDayResult, 'userId' | 'date' | 'changes'>
): Promise<ApplyOptimizeOutcome> {
  try {
    const result = await api.applyDayOptimization({ userId: preview.userId, date: preview.date, changes: preview.changes });
    return { status: 'applied', result };
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 409) {
      const code = getErrorCode(err);
      const details = (err.response.data as { details?: { staleJobIds?: unknown } } | undefined)?.details;
      const staleJobIds = code === 'JOB_CHANGED' && Array.isArray(details?.staleJobIds) ? (details!.staleJobIds as string[]) : [];
      return { status: 'changed', staleJobIds };
    }
    throw err;
  }
}
